import { inject, Injectable } from '@angular/core';
import type { BannerConfiguration } from '@hunting-nature/Secacam/shared/components/show-banner/banner-configuration';
import type { Camera } from '@hunting-nature/shared-core/interfaces/camera.interface';
import { PermissionValues } from '@hunting-nature/shared-core/types/permission.type';
import { Store } from '@ngrx/store';

import { Severity } from '../../../../shared/enums/severity.enum';
import { GeneralActions } from '../../../../shared/redux/general/general.actions';

import type { CameraOverviewStatusBannerTypes } from './camera-overview-status-banner-types.type';
import type { CollectedStatusBanners } from './collected-status-banners.type';

/*
 * Documentation: https://ventrade.atlassian.net/wiki/spaces/ZSEUni/pages/4493803543/Status-Banner+Camera
 * Design:
 *  https://www.figma.com/design/7WtpHWs7qbAJWq6L4OUQjb/Unified-App-Design-Library?node-id=4762-21896&m=dev
 *  https://www.figma.com/design/KONE8m2jowWO2zdBu0hYL0/Gallery-concept-UI-Design?node-id=20-2629&p=f&focus-id=4520-61451&view=focus&m=dev
 */
@Injectable({ providedIn: 'root' })
export class StatusBannerCameraOverviewCollectorService {
  // services
  private readonly store = inject(Store);

  // eslint-disable-next-line max-lines-per-function
  private addErrorLevelBanners(camera: Camera, cameraBanners: Partial<CollectedStatusBanners<CameraOverviewStatusBannerTypes>>): void {
    const externalPowerSourceConnected = camera.powerCordPluggedIn;

    if (!externalPowerSourceConnected && camera.power.status === 'ERROR') {
      const key = 'error-low-battery-overview';
      cameraBanners[key] = [
        {
          id: key + '-' + camera.id,
          severity: Severity.ERROR,
          icon: 'batteries-empty',
          message: 'cameras.statusBanner.batteriesEmpty',
          cameraName: camera.name,
          postponable: true,
          clickHandler: () => {
            this.store.dispatch(
              GeneralActions.goToCameraDetails({
                cameraId: camera.id,
                cameraName: camera.name,
                model: camera.model,
              })
            );
          },
          showCloseButton: true,
        },
      ];
    }

    if (camera.status.pulseActive) {
      const key = 'error-gps-pulse-activated';
      cameraBanners[key] = [
        {
          id: key + '-' + camera.id,
          severity: Severity.ERROR,
          icon: 'out-batteries-soon',
          message: 'cameras.statusBanner.gpsPulseActivated',
          cameraName: camera.name,
          postponable: true,
          clickHandler: () => {
            this.store.dispatch(
              GeneralActions.goToCameraDetails({
                cameraId: camera.id,
                cameraName: camera.name,
                model: camera.model,
              })
            );
          },
          showCloseButton: true,
        },
      ];
    }

    if (camera.storage.status === 'ERROR') {
      const key = 'error-storage-full';
      cameraBanners[key] = [
        {
          id: key + '-' + camera.id,
          severity: Severity.ERROR,
          icon: 'storage-full',
          message: 'cameras.statusBanner.storageFull',
          cameraName: camera.name,
          postponable: true,
          clickHandler: () => {
            this.store.dispatch(
              GeneralActions.goToCameraDetails({
                cameraId: camera.id,
                cameraName: camera.name,
                model: camera.model,
              })
            );
          },
          showCloseButton: true,
        },
      ];
    }

    if (camera.signal.strength === 'STRENGTH_1') {
      const key = 'error-no-signal';
      cameraBanners[key] = [
        {
          id: key + '-' + camera.id,
          severity: Severity.ERROR,
          icon: 'no-signal',
          message: 'cameras.statusBanner.noSignal',
          cameraName: camera.name,
          postponable: true,
          clickHandler: () => {
            this.store.dispatch(
              GeneralActions.goToCameraDetails({
                cameraId: camera.id,
                cameraName: camera.name,
                model: camera.model,
              })
            );
          },
          showCloseButton: true,
        },
      ];
    }
  }

  // eslint-disable-next-line max-lines-per-function
  private addWarningLevelBanners(camera: Camera, cameraBanners: Partial<CollectedStatusBanners<CameraOverviewStatusBannerTypes>>): void {
    const externalPowerSourceConnected = camera.powerCordPluggedIn;

    if (!externalPowerSourceConnected && camera.power.status === 'WARNING') {
      const key = 'warning-out-batteries-soon-overview';
      cameraBanners[key] = [
        {
          id: key + '-' + camera.id,
          severity: Severity.WARNING,
          icon: 'out-batteries-soon',
          message: 'cameras.statusBanner.outBatteriesSoon',
          cameraName: camera.name,
          postponable: true,
          clickHandler: () => {
            this.store.dispatch(
              GeneralActions.goToCameraDetails({
                cameraId: camera.id,
                cameraName: camera.name,
                model: camera.model,
              })
            );
          },
          showCloseButton: true,
        },
      ];
    }

    if (camera.storage.status === 'WARNING') {
      const key = 'warning-storage-nearly-full';
      cameraBanners[key] = [
        {
          id: key + '-' + camera.id,
          severity: Severity.WARNING,
          icon: 'storage-nearly-full',
          message: 'cameras.statusBanner.storageNearlyFull',
          cameraName: camera.name,
          postponable: true,
          clickHandler: () => {
            this.store.dispatch(
              GeneralActions.goToCameraDetails({
                cameraId: camera.id,
                cameraName: camera.name,
                model: camera.model,
              })
            );
          },
          showCloseButton: true,
        },
      ];
    }

    if (camera.signal.strength === 'STRENGTH_2') {
      const key = 'warning-weak-signal';
      cameraBanners[key] = [
        {
          id: key + '-' + camera.id,
          severity: Severity.WARNING,
          icon: 'weak-signal',
          message: 'cameras.statusBanner.weakSignal',
          cameraName: camera.name,
          postponable: true,
          clickHandler: () => {
            this.store.dispatch(
              GeneralActions.goToCameraDetails({
                cameraId: camera.id,
                cameraName: camera.name,
                model: camera.model,
              })
            );
          },
          showCloseButton: true,
        },
      ];
    }
  }

  private addInfoLevelBanners(camera: Camera, cameraBanners: Partial<CollectedStatusBanners<CameraOverviewStatusBannerTypes>>): void {
    const isOwner: boolean = camera.permission === PermissionValues.OWNER;
    if (isOwner && camera.software.updateAvailable === 'RECOMMENDED') {
      const key = 'info-firmware-update-available';
      cameraBanners[key] = [
        {
          id: key + '-' + camera.id,
          severity: Severity.INFO,
          icon: 'firmware-update-available',
          message: 'cameras.statusBanner.firmwareUpdateAvailable',
          cameraName: camera.name,
          postponable: true,
          clickHandler: () => {
            this.store.dispatch(
              GeneralActions.goToCameraDetails({
                cameraId: camera.id,
                cameraName: camera.name,
                model: camera.model,
              })
            );
          },
          showCloseButton: true,
        },
      ];
    }
  }

  private collectBannersForCamera(camera: Camera): Partial<CollectedStatusBanners<CameraOverviewStatusBannerTypes>> {
    const cameraBanners: Partial<CollectedStatusBanners<CameraOverviewStatusBannerTypes>> = {};

    if (camera.permission === PermissionValues.VIEWER) {
      return cameraBanners;
    }

    this.addErrorLevelBanners(camera, cameraBanners);
    this.addWarningLevelBanners(camera, cameraBanners);
    this.addInfoLevelBanners(camera, cameraBanners);

    return cameraBanners;
  }

  private addCollectedBannersToOrdered(
    collectedBanners: Partial<CollectedStatusBanners<CameraOverviewStatusBannerTypes>>,
    orderedBanners: CollectedStatusBanners<CameraOverviewStatusBannerTypes>
  ): void {
    Object.keys(collectedBanners).forEach((key: string) => {
      const typedKey = key as CameraOverviewStatusBannerTypes;
      if (!collectedBanners[typedKey]) {
        return;
      }

      orderedBanners[typedKey].push(...collectedBanners[typedKey]);
    });
  }

  public collect(cameras: Array<Camera>): Array<BannerConfiguration> {
    const orderedBanners: CollectedStatusBanners<CameraOverviewStatusBannerTypes> = {
      'error-low-battery-overview': [],
      'error-gps-pulse-activated': [],
      'error-storage-full': [],
      'error-no-signal': [],
      'warning-out-batteries-soon-overview': [],
      'warning-storage-nearly-full': [],
      'warning-weak-signal': [],
      'info-firmware-update-available': [],
    };

    cameras.forEach((camera: Camera): void => {
      const collectedBanners: Partial<CollectedStatusBanners<CameraOverviewStatusBannerTypes>> = this.collectBannersForCamera(camera);
      this.addCollectedBannersToOrdered(collectedBanners, orderedBanners);
    });

    return Object.values(orderedBanners).flat();
  }
}
